<template>
  <div class="table-footer" :class="isMobile ? 'mobile-footer' : 'd-flex align-center justify-space-between px-4 py-2'">
    <!-- LEFT: Rows per page -->
     <div class="d-flex flex-row align-center gap-5" :class="isMobile && 'flex-wrap'">
       <div class="d-flex align-center">
         <span class="footer-info mr-5">Rows per page</span>
         <v-select
           :value="options.itemsPerPage"
           :items="itemsPerPageOptions"
           dense
           hide-details
           class="rows-per-page-select font-weight-bold"
           @change="onItemsPerPageChange"
         />
       </div>

       <div class="footer-info">
         <span v-if="pagination.itemsLength > 0">
           Showing {{ displayStart }} to {{ displayStop }} of {{ pagination.itemsLength }} Records
         </span>
         <span v-else-if="pagination.itemsLength === 0">No Records</span>
       </div>
     </div>

    <!-- RIGHT: Pagination controls -->
    <div class="d-flex align-center pagination-controls" :class="isMobile && 'flex-wrap justify-center mt-2'" v-if="pagination.pageCount > 0">
      <!-- Left arrow -->
      <v-btn
        depressed
        :disabled="currentPage === 1"
        class="mx-2 pagination-btn pagination-arrow"
        @click="goToPage(currentPage - 1)"
      >
        <v-icon small>mdi-chevron-left</v-icon>
      </v-btn>

      <template v-for="(item, index) in visiblePages">
        <!-- Page button -->
        <v-btn v-if="item.type === 'page'"
          :key="'page-' + item.value"
          small
          depressed
          class="mx-1 pagination-btn font-weight-bold"
          :class="item.value === currentPage ? 'active-page' : 'inactive-page'"
          @click="goToPage(item.value)"
        >
          {{ item.value }}
        </v-btn>

        <!-- Ellipsis dropdown -->
        <v-menu v-else-if="item.type === 'ellipsis'"
          :key="'ellipsis-' + index"
          offset-y
          max-height="333"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              depressed
              min-width="32"
              height="32"
              class="mx-1 pagination-btn inactive-page"
              v-bind="attrs"
              v-on="on"
            >
              ....
            </v-btn>
          </template>
          <v-list dense class="ellipsis-dropdown">
            <v-list-item
              v-for="page in item.pages"
              :key="page"
              @click="goToPage(page)"
            >
              <v-list-item-title class="text-center">{{ page }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <!-- Right arrow -->
      <v-btn
        depressed
        :disabled="currentPage >= pagination.pageCount"
        class="mx-1 pagination-btn pagination-arrow"
        @click="goToPage(currentPage + 1)"
      >
        <v-icon small>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
  name: "TableFooter",
  props: {
    pagination: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [10, 20, 50, 100],
    },
  },
  data() {
    return {
      currentPage: 1,
    };
  },
  created() {
    this.currentPage = this.options.page;
    this.debouncedEmit = debounce((options) => {
      this.$emit("update-options", options);
    }, 500);
  },
  beforeDestroy() {
    if (this.debouncedEmit) this.debouncedEmit.cancel();
  },
  watch: {
    'options.page'(newPage) {
      this.currentPage = newPage;
    },
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    displayStart() {
      if (this.pagination.itemsLength === 0) return 0;
      return this.pagination.pageStart + 1;
    },
    displayStop() {
      return Math.min(this.pagination.pageStop, this.pagination.itemsLength);
    },
    visiblePages() {
      const total = this.pagination.pageCount;
      const current = this.currentPage;

      if (total <= 0) return [];

      // 5 or fewer pages — show all directly
      if (total <= 5) {
        return Array.from({ length: total }, (_, i) => ({
          type: "page",
          value: i + 1,
        }));
      }

      // Current page is in the first or last 2
      if (current <= 2 || current >= total - 1) {
        return [
          { type: "page", value: 1 },
          { type: "page", value: 2 },
          { type: "ellipsis", pages: this.buildPageRange(3, total - 2) },
          { type: "page", value: total - 1 },
          { type: "page", value: total },
        ];
      }

      // Middle pages — show: 1 ... current ... max
      return [
        { type: "page", value: 1 },
        { type: "ellipsis", pages: this.buildPageRange(2, current - 1) },
        { type: "page", value: current },
        { type: "ellipsis", pages: this.buildPageRange(current + 1, total - 1) },
        { type: "page", value: total },
      ];
    },
  },
  methods: {
    goToPage(page) {
      if (page < 1 || page > this.pagination.pageCount) return;
      this.currentPage = page;
      this.debouncedEmit({ ...this.options, page });
    },
    onItemsPerPageChange(itemsPerPage) {
      this.currentPage = 1;
      this.debouncedEmit({ ...this.options, page: 1, itemsPerPage });
    },
    buildPageRange(start, end) {
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
};
</script>

<style scoped>
.table-footer {
  border-top: thin solid rgba(0, 0, 0, 0.12);
  font-size: 0.875rem;
  min-height: 36px;
  max-width: 100%;
  box-sizing: border-box;
}

.mobile-footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 8px 12px;
  gap: 4px;
}

.footer-info {
  color: var(--primary) !important;
  font-weight: 400;
  font-size: 0.875rem;
  white-space: nowrap;
}

.rows-per-page-select {
  max-width: 44px;
  font-size: 0.875rem;
  margin: 0 !important;
  padding: 0 !important;
}

.rows-per-page-select >>> .v-input__control {
  min-height: auto !important;
}

.rows-per-page-select >>> .v-input__slot {
  min-height: auto !important;
  padding: 0 !important;
  margin-bottom: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  align-items: center;
}

.rows-per-page-select >>> .v-input__slot::before,
.rows-per-page-select >>> .v-input__slot::after {
  display: none !important;
}

.rows-per-page-select >>> fieldset {
  border: none !important;
}

.rows-per-page-select >>> .v-select__selections {
  padding: 0 !important;
  flex-wrap: nowrap;
}

.rows-per-page-select >>> .v-select__selection {
  font-size: 0.875rem;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1;
}

.rows-per-page-select >>> .v-input__append-inner {
  margin: 0 0 0 -2px !important;
  padding: 0 !important;
  align-self: center;
}

.rows-per-page-select >>> .v-input__append-inner .v-input__icon {
  width: 16px !important;
  min-width: 16px !important;
}

.rows-per-page-select >>> .v-input__append-inner .v-icon {
  font-size: 18px !important;
}

.pagination-btn {
  min-width: 32px !important;
  height: 32px !important;
  padding: 4px !important;
  border-radius: 8px !important;
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.active-page {
  background-color: var(--blue-01) !important;
  color: var(--white) !important;
}

.inactive-page {
  background-color: var(--gray-02) !important;
  color: var(--gray) !important;
}

.pagination-arrow {
  background-color: var(--blue-02) !important;
  color: #000000DE !important;
}

.pagination-arrow.v-btn--disabled {
  background-color: var(--blue-02) !important;
  color: rgba(0, 0, 0, 0.2) !important;
}

.ellipsis-dropdown .v-list-item {
  min-height: 32px;
}

.ellipsis-dropdown .v-list-item__title {
  font-size: 0.875rem;
}
.gap-5{
  gap: 20px;
}
</style>

<style>
.ellipsis-dropdown.v-list {
  position: relative;
}

.ellipsis-dropdown.v-list::after {
  content: '';
  position: sticky;
  bottom: 0;
  display: block;
  height: 12px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent);
  pointer-events: none;
}
</style>
